import { useNavigate } from "react-router";

import TileTitle from "ds/components/Tile/Title";
import { IconComponent } from "types/Icon";
import TileWrapper from "ds/components/Tile/Wrapper";
import TileContent from "ds/components/Tile/Content";
import IconEmphasized from "ds/components/IconEmphasized";
import { IconEmphasizedColor } from "ds/components/IconEmphasized/types";
import Box from "ds/components/Box";
import { AnalyticsPageLaunchpad } from "hooks/useAnalytics/pages/launchpad";
import useAnalytics from "hooks/useAnalytics";
import Icon from "ds/components/Icon";
import { Clock, TickCircleFilled } from "components/icons/generated";
import Typography from "ds/components/Typography";
import ProgressBar from "ds/components/ProgressBar";
import Badge from "ds/components/Badge";
import { UserGuideGroupSkillLevel } from "types/generated";
import Tooltip from "ds/components/Tooltip";
import TextEllipsis from "ds/components/TextEllipsis";

import { formatSkillLevel } from "./utils";
import styles from "./styles.module.css";

type LaunchPadLearnGroupTileProps = {
  id: string;
  title: string;
  icon: IconComponent;
  iconColor: IconEmphasizedColor;
  to: string;
  skillLevel?: UserGuideGroupSkillLevel;
  description: string;
  timeToComplete: string;
  guidesCount: number;
  guidesCompletedCount: number;
};

const LaunchPadLearnGroupTile = ({
  id,
  title,
  icon,
  iconColor,
  to,
  skillLevel,
  description,
  timeToComplete,
  guidesCount,
  guidesCompletedCount,
}: LaunchPadLearnGroupTileProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageLaunchpad.Launchpad,
  });

  const navigate = useNavigate();

  const handleClick = () => {
    trackSegmentAnalyticsEvent(`Learn ${title} clicked`);
    navigate(to);
  };

  const badgeText = !guidesCount ? "Coming soon" : formatSkillLevel(skillLevel);
  const isComplete = guidesCompletedCount === guidesCount;

  return (
    <TileWrapper
      onClick={handleClick}
      className={styles.groupTile}
      data-userflow-id={`learn-mission-${id}`}
    >
      <TileContent direction="row" align="center" gap="large" className={styles.groupTileContent}>
        <Box direction="row" align="center" gap="large" grow="1" zeroMinWidth>
          <IconEmphasized icon={icon} color={iconColor} />

          <Box direction="column" gap="small" grow="1" zeroMinWidth>
            <Box direction="row" align="center" gap="small">
              <TileTitle>{title}</TileTitle>
              <Badge type="weak" variant="gray" text={badgeText} />
            </Box>
            <TextEllipsis tooltip={description} tooltipWidthMode="maxWidthSm">
              {(props) => (
                <Typography {...props} tag="span" variant="p-body3">
                  {description}
                </Typography>
              )}
            </TextEllipsis>
          </Box>
        </Box>

        {guidesCount > 0 && (
          <Box
            direction="column"
            align="end"
            gap="small"
            shrink="0"
            className={styles.groupTileProgress}
          >
            <ProgressBar
              ariaLabel="Scenario group completion progress"
              max={guidesCount}
              current={guidesCompletedCount}
              isPercentage
            />
            {isComplete ? (
              <Box direction="row" align="center" gap="small">
                <Icon src={TickCircleFilled} color="secondary" />
                <Typography color="secondary" variant="p-body3" tag="span">
                  Complete
                </Typography>
              </Box>
            ) : (
              <Box direction="row" align="center" gap="small">
                <Icon src={Clock} color="secondary" />
                <Tooltip
                  on={(props) => (
                    <Typography {...props} color="secondary" variant="p-body3" tag="span">
                      {timeToComplete} left
                    </Typography>
                  )}
                >
                  <Typography color="secondary" variant="p-body3" tag="span">
                    Estimated time to complete: {timeToComplete} left
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
      </TileContent>
    </TileWrapper>
  );
};

export default LaunchPadLearnGroupTile;
